import React, { useEffect, useRef, useState } from 'react';
import axiosInstance from '../utils/axiosInstance';
import config from '../utils/config';
import { Device } from '../types';
import { useAuth } from '../context/AuthContext';
import { useFirestoreEnrichment } from '../hooks/useFirestoreEnrichment';
import { useEntityPathOptions } from '../hooks/useEntityPathOptions';
import { validateEntityPath } from './EntityPathField';
import ClipInventoryBox from './ClipInventoryBox';
import DeviceAppUserData from './DeviceAppUserData';
import DeviceRecordFields from './DeviceRecordFields';
import { deviceDraftChanged, deviceUpdateBody, entityPathChanged } from '../utils/deviceEdit';
import Button from './ui/Button';
import ActionRow from './ui/ActionRow';
import { extractErrorMessage } from '../utils/extractErrorMessage';
import axios from 'axios';
import AnalysisParametersSection from './AnalysisParametersSection';
import ReferenceUploadsPanel from './ReferenceUploadsPanel';

interface DeviceSettingsPanelProps {
  device: Device;
  /** Reload the device detail from the API (the workspace's refetch). */
  onDeviceChanged?: () => void;
}

/**
 * Plan 94 Phase 2 - route-bound device Settings (editable RDS fields).
 *
 * Renders the same fields as the Device List Edit dialog (DeviceRecordFields,
 * FS-1074), bound to a single device and saved in place. Visible with
 * `read_devices`; the form is read-only and the Save button hidden unless the
 * operator holds `write_devices`.
 */
const DeviceSettingsPanel: React.FC<DeviceSettingsPanelProps> = ({ device, onDeviceChanged }) => {
  const { hasPermission, scopes } = useAuth();
  const canWrite = hasPermission('write_devices');
  const { enrichmentMap, loading: enrichmentLoading, error: enrichmentError } = useFirestoreEnrichment();
  const { options: entityPathOptions, error: entityPathOptionsError } = useEntityPathOptions(canWrite);

  const [draft, setDraft] = useState<Device>(device);
  // FS-890: what the draft is compared with — the device as loaded, then as
  // last saved — so Save is for a real change and an unchanged path is not sent.
  const [baseline, setBaseline] = useState<Device>(device);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [pathError, setPathError] = useState('');
  // Counts the records loaded into the form. The workspace refreshes the
  // device in place (same id, no remount), so a save can be answered after a
  // newer record arrived; that answer must not put its older draft back as
  // the baseline.
  const loadedGeneration = useRef(0);

  // Reset the draft whenever the workspace switches devices or reloads one.
  useEffect(() => {
    loadedGeneration.current += 1;
    setDraft(device);
    setBaseline(device);
    setSaved(false);
    setError('');
    setPathError('');
  }, [device]);

  const enrichment = enrichmentMap[draft.name];

  const handleChange = (patch: Partial<Device>) => {
    setDraft((current) => ({ ...current, ...patch }));
    setSaved(false);
    setPathError('');
  };

  const handleSave = async () => {
    if (pathProblem) {
      setPathError(`Cannot save settings: ${pathProblem}`);
      return;
    }
    const sent = draft;
    const generation = loadedGeneration.current;
    setSaving(true);
    setError('');
    try {
      await axiosInstance.put(`${config.devicesApiUrl}/devices/${sent.id}`, deviceUpdateBody(sent, baseline));
      // Edits made while the save was in flight stay in the draft as changes.
      // If a newer record was loaded meanwhile, it is the baseline already.
      if (generation === loadedGeneration.current) {
        setBaseline(sent);
        setSaved(true);
      }
    } catch (err: unknown) {
      console.error('Error saving device:', err);
      // Surface the server's reason verbatim when it sent one — the filter
      // threshold rules (FS-443) are only checkable server-side, and "Failed
      // to save" gives the operator nothing to correct.
      const reason = axios.isAxiosError(err) ? err.response?.data?.message : null;
      setError(typeof reason === 'string' ? reason : extractErrorMessage(err, 'Failed to save device settings.'));
    } finally {
      setSaving(false);
    }
  };

  // FS-890/FS-911: a changed path must be a path, inside the caller's scope;
  // an unchanged one is not sent, so it is not checked.
  const pathProblem = entityPathChanged(draft, baseline)
    ? validateEntityPath(draft.entity_path ?? '', scopes)
    : null;
  const changed = deviceDraftChanged(draft, baseline);
  const saveBlocked = saving
    ? 'Saving the settings to this device…'
    : changed ? null : 'Nothing to save: no setting has changed.';

  return (
    <div>
      <ClipInventoryBox key={`inventory-${device.id}`} device={device} canWrite={canWrite} onChanged={onDeviceChanged} />

      <DeviceAppUserData enrichment={enrichment} loading={enrichmentLoading} error={enrichmentError} />
      <DeviceRecordFields
        draft={draft}
        onChange={handleChange}
        canWrite={canWrite}
        addressLocked={!!enrichment}
        entityPathOptions={entityPathOptions}
        entityPathOptionsError={entityPathOptionsError}
        scopes={scopes ?? []}
      />

      {(error || pathError) && <div role="alert" className="border border-status-error-border bg-status-error text-status-error-foreground p-3 rounded-md mb-4">{error || pathError}</div>}
      {saved && <div className="border border-status-success-border bg-status-success text-status-success-foreground p-3 rounded-md mb-4">Settings saved.</div>}

      {canWrite ? (
        <ActionRow reason={saveBlocked} reasonId="settings-save-reason">
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={!!saveBlocked}
            aria-describedby={saveBlocked ? 'settings-save-reason' : undefined}
            title={saveBlocked ?? "Save the settings above to this device's record"}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </ActionRow>
      ) : (
        <p className="text-sm text-muted-foreground">
          You have read-only access. Editing device settings requires the{' '}
          <code className="px-1 bg-gray-100 rounded-sm font-mono text-xs">write_devices</code> permission.
        </p>
      )}
      {hasPermission('manage_analysis_runs') && <AnalysisParametersSection key={device.id} deviceId={device.id} />}
      {hasPermission('upload_reference_data') && <ReferenceUploadsPanel deviceName={device.name} />}
    </div>
  );
};

export default DeviceSettingsPanel;
